import { AiReviewStatus, JobBillingStatus } from '@treadinc/horizon-api-spec';

export enum ChargesViewTab {
  ALL = 'all',
  SETTLED = 'settled',
}

export enum ChargesShowOption {
  ANY = 'any',
  APPROVED = 'approved',
  NEEDS_REVIEW = 'needs_review',
  UNAPPROVED = 'unapproved',
}

export enum ChargesSettledSide {
  BOTH = 'both',
  PAYABLE = 'payable',
  RECEIVABLE = 'receivable',
}

export enum ChargesException {
  ISSUES = 'issues',
  REJECTED = 'rejected',
}

export enum ChargesGrainTab {
  HOURLY_JOBS = 'hourly_jobs',
  LOADS = 'loads',
}

export const CHARGES_VIEW_TABS = [ChargesViewTab.ALL, ChargesViewTab.SETTLED];

export const CHARGES_SHOW_OPTIONS = [
  ChargesShowOption.ANY,
  ChargesShowOption.UNAPPROVED,
  ChargesShowOption.APPROVED,
];

export const CHARGES_SETTLED_SIDES = [
  ChargesSettledSide.BOTH,
  ChargesSettledSide.PAYABLE,
  ChargesSettledSide.RECEIVABLE,
];

export const CHARGES_GRAIN_TABS = [ChargesGrainTab.LOADS, ChargesGrainTab.HOURLY_JOBS];

export interface ChargesView {
  exception: ChargesException | null;
  isAiApprovedOnly: boolean;
  settledSide: ChargesSettledSide;
  showOption: ChargesShowOption;
  viewTab: ChargesViewTab;
}

export const DEFAULT_CHARGES_VIEW: ChargesView = {
  exception: null,
  isAiApprovedOnly: false,
  settledSide: ChargesSettledSide.BOTH,
  showOption: ChargesShowOption.ANY,
  viewTab: ChargesViewTab.ALL,
};

const leaveRejected = (exception: ChargesException | null) =>
  exception === ChargesException.REJECTED ? null : exception;

export const selectViewTab = (
  view: ChargesView,
  viewTab: ChargesViewTab,
): ChargesView => ({
  ...view,
  exception: leaveRejected(view.exception),
  viewTab,
});

export const selectSettledSide = (
  view: ChargesView,
  settledSide: ChargesSettledSide,
): ChargesView => ({ ...view, exception: leaveRejected(view.exception), settledSide });

export function selectShowOption(
  view: ChargesView,
  showOption: ChargesShowOption,
  issueTypes: ChargesIssueType[],
): ChargesView {
  return {
    ...view,
    exception: isNeedsReview(view, issueTypes) ? null : leaveRejected(view.exception),
    showOption,
    viewTab: ChargesViewTab.ALL,
  };
}

export function toggleException(
  view: ChargesView,
  exception: ChargesException,
): ChargesView {
  return { ...view, exception: view.exception === exception ? null : exception };
}

export const toggleAiApprovedOnly = (view: ChargesView): ChargesView => ({
  ...view,
  isAiApprovedOnly: !view.isAiApprovedOnly,
});

export const getHighlightedViewTab = (
  view: Pick<ChargesView, 'exception' | 'viewTab'>,
) => (view.exception === ChargesException.REJECTED ? null : view.viewTab);

const isNeedsReview = (
  view: Pick<ChargesView, 'exception' | 'showOption'>,
  issueTypes: ChargesIssueType[],
) =>
  view.showOption === ChargesShowOption.UNAPPROVED &&
  view.exception === ChargesException.ISSUES &&
  issueTypes.length === NEEDS_REVIEW_ISSUE_TYPES.length &&
  NEEDS_REVIEW_ISSUE_TYPES.every((issueType) => issueTypes.includes(issueType));

export const getShownOption = (
  view: Pick<ChargesView, 'exception' | 'showOption'>,
  issueTypes: ChargesIssueType[],
) => (isNeedsReview(view, issueTypes) ? ChargesShowOption.NEEDS_REVIEW : view.showOption);

const billingStatesByShowOption = {
  [ChargesShowOption.ANY]: undefined,
  [ChargesShowOption.APPROVED]: undefined,
  [ChargesShowOption.NEEDS_REVIEW]: [JobBillingStatus.TO_APPROVE],
  [ChargesShowOption.UNAPPROVED]: [JobBillingStatus.TO_APPROVE],
} satisfies Record<ChargesShowOption, JobBillingStatus[] | undefined>;

const billingStatesBySettledSide = {
  [ChargesSettledSide.BOTH]: [
    JobBillingStatus.PAYABLE_IN_SETTLEMENT,
    JobBillingStatus.RECEIVABLE_IN_SETTLEMENT,
    JobBillingStatus.PAYABLE_PAID,
    JobBillingStatus.RECEIVABLE_PAID,
  ],
  [ChargesSettledSide.PAYABLE]: [
    JobBillingStatus.PAYABLE_IN_SETTLEMENT,
    JobBillingStatus.PAYABLE_PAID,
  ],
  [ChargesSettledSide.RECEIVABLE]: [
    JobBillingStatus.RECEIVABLE_IN_SETTLEMENT,
    JobBillingStatus.RECEIVABLE_PAID,
  ],
} satisfies Record<ChargesSettledSide, JobBillingStatus[]>;

// The backend ORs these states. Rejected mode ignores the last tab selected.
export function getBillingStates(view: ChargesView) {
  if (view.exception === ChargesException.REJECTED) {
    return [JobBillingStatus.REJECTED];
  }

  return view.viewTab === ChargesViewTab.SETTLED
    ? billingStatesBySettledSide[view.settledSide]
    : billingStatesByShowOption[view.showOption];
}

export const isShowingApprovedWork = (view: ChargesView) =>
  view.viewTab === ChargesViewTab.ALL &&
  view.showOption === ChargesShowOption.APPROVED &&
  view.exception !== ChargesException.REJECTED;

export const getAiReviewStatuses = (view: ChargesView) =>
  view.isAiApprovedOnly &&
  view.viewTab === ChargesViewTab.ALL &&
  view.exception !== ChargesException.REJECTED
    ? [AiReviewStatus.APPROVED]
    : undefined;

export const hidesAllRejectedLoads = (
  view: Pick<ChargesView, 'exception' | 'showOption' | 'viewTab'>,
) =>
  view.viewTab === ChargesViewTab.ALL &&
  view.showOption === ChargesShowOption.UNAPPROVED &&
  view.exception !== ChargesException.REJECTED;

export enum ChargesIssueType {
  AI_FLAGGED = 'ai_flagged',
  DUPLICATE_TICKETS = 'duplicate_tickets',
  FLAGGED_TICKETS = 'flagged_tickets',
  MISSING_QUANTITY = 'missing_quantity',
  MISSING_TICKET = 'missing_ticket',
}

export const CHARGES_ISSUE_TYPES = [
  ChargesIssueType.DUPLICATE_TICKETS,
  ChargesIssueType.AI_FLAGGED,
  ChargesIssueType.MISSING_TICKET,
  ChargesIssueType.MISSING_QUANTITY,
  ChargesIssueType.FLAGGED_TICKETS,
];

// Only duplicate tickets has a backend filter today; the rest show as coming soon.
export const AVAILABLE_CHARGES_ISSUE_TYPES = [ChargesIssueType.DUPLICATE_TICKETS];

export const NEEDS_REVIEW_ISSUE_TYPES = [
  ChargesIssueType.DUPLICATE_TICKETS,
  ChargesIssueType.MISSING_TICKET,
  ChargesIssueType.MISSING_QUANTITY,
].filter((issueType) => AVAILABLE_CHARGES_ISSUE_TYPES.includes(issueType));

export function deriveIssueFilters(isOn: boolean, issueTypes: ChargesIssueType[]) {
  return {
    ticketPotentialDuplicate:
      isOn && issueTypes.includes(ChargesIssueType.DUPLICATE_TICKETS)
        ? ['true']
        : undefined,
  };
}
